
import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Todo } from '@/types/todo';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { setPendingPayment } from '@/utils/paywallHelpers';
import PaywallContent from './PaywallContent';

interface PaywallModalProps {
  isOpen: boolean;
  todos: Todo[];
  completedTodos: Todo[];
  onClose: () => void;
}

const PaywallModal = ({
  isOpen,
  todos,
  completedTodos,
  onClose
}: PaywallModalProps) => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);

  const handlePayment = async (option: any) => {
    setIsProcessingPayment(true);
    setSelectedAmount(option.amount);
    try {
      // Call Stripe edge function to create checkout session
      const {
        data,
        error
      } = await supabase.functions.invoke('create-payment', {
        body: {
          amount: option.amount
        }
      });
      if (error) {
        throw new Error(error.message || 'Failed to create payment session');
      }
      if (!data?.url) {
        throw new Error('No checkout URL received');
      }

      // Store the payment details for when user returns from Stripe
      localStorage.setItem('pendingPayment', JSON.stringify({
        amount: option.amount,
        sessionId: data.sessionId,
        type: option.type,
        days: option.days || undefined
      }));

      // Set pending payment state
      setPendingPayment();

      // Redirect to Stripe checkout
      window.location.href = data.url;
      toast({
        title: "Redirecting to Payment",
        description: "Redirecting to Stripe checkout..."
      });
    } catch (error) {
      toast({
        title: "Payment Failed",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsProcessingPayment(false);
      setSelectedAmount(null);
    }
  };

  return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg p-4 sm:p-6" onEscapeKeyDown={e => e.preventDefault()} onPointerDownOutside={e => e.preventDefault()} onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2 text-warning">
              <AlertTriangle className="h-5 w-5" />
              End of Day: Overdue Tasks
            </DialogTitle>
          </DialogHeader>

          <PaywallContent
            todos={todos}
            completedTodos={completedTodos}
            isProcessingPayment={isProcessingPayment}
            selectedAmount={selectedAmount}
            onPayment={handlePayment}
          />
      </DialogContent>
    </Dialog>
  );
};

export default PaywallModal;
